'use strict';
import angular from 'angular';
import moment from 'moment';
import $ from 'jquery';
import sanitizeHtmlLib from 'sanitize-html';
import { SvgCacheBust } from 'coreModules/shared/scripts/app.constants';

angular.module('core.app.filters', [])

    .filter('iif', iif)
    .filter('widgetNumber', widgetNumber)
    .filter('capitalize', capitalize)
    .filter('sanitizeHtml', sanitizeHtml)
    .filter('sanitizeRichHtml', sanitizeRichHtml)
    .filter('trustUrl', trustUrl)
    .filter('formatDate', formatDate)
    .filter('osSpecificCommandButton', osSpecificCommandButton)
    .filter('svgCacheBust', svgCacheBust);


//
// Repaired GCS SVGs kept their bytes, so their ETag is unchanged and a browser holding the
// pre-repair response revalidates to a 304 and goes on decoding them as JPEG. A distinct url is
// the only thing a cache treats as new (TA-51680).
//
function svgCacheBust() {
    return function(url) {
        if (typeof url !== 'string' || !url) {
            return url;
        }
        // A url that already carries a query may be signed, and appending would invalidate it.
        if (url.indexOf('?') !== -1
            || url.indexOf(SvgCacheBust.HOST) === -1
            || !url.toLowerCase().endsWith(SvgCacheBust.EXTENSION)) {
            return url;
        }

        return url + '?' + SvgCacheBust.PARAM + '=' + SvgCacheBust.TOKEN;
    };
}

//
// Ternary operator filter
//
function iif() {
    return function(input, trueValue, falseValue) {
        return input ? trueValue : falseValue;
    };
}

//
// Formatting for widget numbers
//
function widgetNumber() {
    return function(val, format) {
        var decimals = format == 'percent' || format == 'currency' || format == 'decimal'? 2 : 0;
        var digit_reduce = format == 'decimal' ? null : 2;
        return $.fn.formatNumber(val, format, decimals, digit_reduce);
    };
}

//
// Capitalize first letter of word
//
function capitalize() {
    return function(input) {
        return (!!input) ? input.replace(/([^\W_]+[^\s-]*) */g, function(txt){
            return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
        }) : '';
    };
}

/**
 * Sanitize HTML via ngSanitize, then wrap as $sce-trusted so the
 * bind-html-compile directive accepts it. Replaces the unsafe trustHtml
 * filter that bypassed sanitization entirely (TR-7030). Use for titles and
 * plain text — ngSanitize strips inline styles, so do NOT use for rich
 * media/executive-summary content (see sanitizeRichHtml).
 * @ngInject
 */
function sanitizeHtml($sce, $sanitize) {
    return function(html) {
        return $sce.trustAsHtml($sanitize(html || ''));
    };
}

// Only declaration chars / markup chars are disallowed; values like
// rgb(43,104,129), 36px, 1.2 all pass.
const SAFE_CSS_VALUE = /^[^<>{};]+$/i;
// As SAFE_CSS_VALUE but rejects url(...) unless it points at http(s)/data.
// (?=(['"]?))\1 atomically consumes the optional opening quote so the scheme
// check lands after it — without this, quoted url('https://...') is wrongly stripped.
const SAFE_CSS_URL_VALUE = /^(?!.*url\s*\(\s*(?=(['"]?))\1(?!https?:|data:))[^<>{};]+$/i;

const RICH_HTML_STYLE_ALLOWLIST = ['color', 'background-color', 'text-align', 'text-decoration',
    'text-transform', 'text-indent', 'font', 'font-size', 'font-weight', 'font-family',
    'font-style', 'line-height', 'letter-spacing', 'white-space', 'padding', 'padding-top',
    'padding-right', 'padding-bottom', 'padding-left', 'margin', 'margin-top', 'margin-right',
    'margin-bottom', 'margin-left', 'border', 'border-top', 'border-right', 'border-bottom',
    'border-left', 'border-color', 'border-width', 'border-style', 'border-radius', 'width',
    'height', 'max-width', 'min-width', 'max-height', 'min-height', 'display', 'float',
    'vertical-align', 'list-style-type', 'box-sizing', 'opacity', 'text-shadow',
    'box-shadow', 'position', 'top', 'right', 'bottom', 'left', 'inset', 'z-index',
    'overflow', 'overflow-x', 'overflow-y', 'pointer-events', 'cursor', 'visibility',
    'filter', 'transform', 'transition', 'animation', 'background-position',
    'background-size', 'background-repeat', 'object-fit', 'object-position', 'gap',
    'flex', 'flex-direction', 'flex-wrap', 'flex-grow', 'flex-shrink', 'flex-basis',
    'justify-content', 'align-items', 'align-content', 'align-self', 'order'].reduce((styles, prop) => {
    styles[prop] = [SAFE_CSS_VALUE];
    return styles;
}, {
    // url()-capable properties get the scheme-restricted matcher.
    background: [SAFE_CSS_URL_VALUE],
    'background-image': [SAFE_CSS_URL_VALUE],
    'list-style': [SAFE_CSS_URL_VALUE],
});

// sanitize-html config that preserves the inline styling, <style>/<link>/<meta>
// blocks, and embeds media/ES content relies on, while still discarding
// <script>, on*= handlers, and javascript: URLs. ngSanitize cannot keep
// `style`, which is why media/ES content broke after TR-7030.
// NOTE: <style>/<link> are rendered into the live app DOM here (no iframe/shadow
// sandbox like NUI), so author CSS is page-global — an accepted XSS/clickjacking
// tradeoff to restore rich media rendering in the old UI and PDF export.
const RICH_HTML_SANITIZE_OPTIONS = {
    allowedTags: sanitizeHtmlLib.defaults.allowedTags.concat([
        'img', 'font', 'span', 'iframe', 'video', 'source', 'u', 's', 'del', 'ins',
        'style', 'link', 'meta',
    ]),
    // Permit <style>/<link> despite sanitize-html's vulnerable-tag warning.
    allowVulnerableTags: true,
    allowedAttributes: {
        '*': ['style', 'class', 'id', 'align', 'dir', 'title'],
        a: ['href', 'target', 'rel', 'name'],
        img: ['src', 'alt', 'width', 'height'],
        font: ['color', 'face', 'size'],
        iframe: ['src', 'width', 'height', 'frameborder', 'allow', 'allowfullscreen'],
        video: ['src', 'width', 'height', 'controls', 'autoplay', 'loop', 'muted', 'poster'],
        source: ['src', 'type'],
        style: ['type', 'media'],
        link: ['href', 'rel', 'type', 'media', 'crossorigin'],
        meta: ['charset', 'name', 'content', 'property'],
        table: ['width', 'border', 'cellpadding', 'cellspacing'],
        td: ['colspan', 'rowspan', 'width', 'height', 'valign', 'align'],
        th: ['colspan', 'rowspan', 'width', 'height', 'valign', 'align'],
    },
    allowedStyles: { '*': RICH_HTML_STYLE_ALLOWLIST },
    // data: stays off the global list (no data:text/html on links/iframes) and
    // is granted only to <img> for inline base64 images.
    allowedSchemes: ['http', 'https', 'mailto', 'tel'],
    allowedSchemesByTag: { img: ['http', 'https', 'data'], iframe: ['http', 'https'], link: ['http', 'https'] },
    allowedSchemesAppliedToAttributes: ['href', 'src'],
    // Anti reverse-tabnabbing: target="_blank" links get rel=noopener so the
    // opened page can't reach window.opener.
    transformTags: {
        a: (tagName, attribs) => {
            if (attribs.target === '_blank') {
                attribs.rel = 'noopener noreferrer';
            }
            return { tagName, attribs };
        },
    },
};

/**
 * Sanitize rich media / executive-summary content while preserving inline
 * styles and embeds, then wrap as $sce-trusted for bind-html-compile.
 * @ngInject
 */
function sanitizeRichHtml($sce) {
    return function(html) {
        return $sce.trustAsHtml(sanitizeHtmlLib(html || '', RICH_HTML_SANITIZE_OPTIONS));
    };
}

/**
 * If from a trusted source, allow unsafe src. Ex: <iframe/>
 * @ngInject
 */
function trustUrl($sce) {
    return function(url) {
        return $sce.trustAsResourceUrl(url);
    };
}

/**
 * Returns a pretty formatted date
 * @ngInject
 */
function formatDate(LanguageService) {
    return function(date) {
        return moment.unix(date).isValid()
            ? moment.unix(date).utc().format(LanguageService.getDisplayDateFormat())
            : moment(date).format(LanguageService.getDisplayDateFormat());
    };
}

function osSpecificCommandButton() {
    return function(inputString) {
        var isMacOS = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
        return inputString.replace(new RegExp('{OS_SPECIFIC_COMMAND}', 'g'), isMacOS ? 'CMD' : 'CTRL');
    };
}